import type { ChartEffectIds } from "./useChartEffectIds";

/**
 * Depth for the Recharts charts, in the manner of the signed-out page: bars that fade from their series colour towards the panel,
 * an area under a running total, lines that glow a little, and a brief pulse on the point that matters now. Each chart renders its
 * own {@link ChartDefs}, because SVG ids are per document and a page holds several charts.
 */

/** A series to make a gradient for: its key, its colour, and how opaque the gradient is at its top and at its foot. */
export interface ChartGradient {
  key: string;
  color: string;
  top: number;
  bottom: number;
}

/**
 * The `<defs>` of one chart: a vertical gradient for each series given, and a soft glow for lines. The glow's filter region is in
 * user space, not the line's bounding box, so a flat line, whose box has no height, is still drawn.
 *
 * @param props.ids - The chart's effect ids.
 * @param props.gradients - The series to make gradients for.
 * @returns The chart's definitions.
 */
export function ChartDefs({ ids, gradients }: { ids: ChartEffectIds; gradients: readonly ChartGradient[] }): React.JSX.Element {
  return (
    <defs>
      {gradients.map((gradient) => (
        <linearGradient key={gradient.key} id={ids.gradient(gradient.key)} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: gradient.color, stopOpacity: gradient.top }} />
          <stop offset="100%" style={{ stopColor: gradient.color, stopOpacity: gradient.bottom }} />
        </linearGradient>
      ))}
      <filter id={ids.glow} filterUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

/**
 * The shape of a `ReferenceDot` that marks a point that matters now: a dot ringed in the panel's colour, with a ring that pulses
 * out from it a few times when the chart appears (`.chart-pulse` in global.css), and not at all for a viewer who prefers reduced
 * motion.
 *
 * @param props.cx - The dot's x, from Recharts.
 * @param props.cy - The dot's y, from Recharts.
 * @param props.fill - The dot's colour.
 * @returns The dot, or an empty group until Recharts has placed it.
 */
export function PulseDot({ cx, cy, fill }: { cx?: number; cy?: number; fill?: string }): React.JSX.Element {
  if (cx === undefined || cy === undefined) {
    return <g />;
  }
  return (
    <g>
      <circle className="chart-pulse" cx={cx} cy={cy} r={4} fill={fill} />
      <circle cx={cx} cy={cy} r={4.5} fill={fill} stroke="var(--color-page-bg)" strokeWidth={2} />
    </g>
  );
}
